import React, { useState, useRef, useCallback, useEffect } from "react";
import { IconButton, Box, Typography } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import { formatMinutes } from "utils/formatTime";

const VoiceRecorder = ({ setAudioObjArr }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0); // Time in seconds

  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null); // Ref for interval timer
  const [audioChunks, setAudioChunks] = useState([]);

  const startRecording = useCallback(async () => {
    try {
      setIsRecording(true);
      setRecordingTime(0); // Reset recording time

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorderRef.current.start();

      let localAudioChunks = [];
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (typeof event.data === "undefined") return;
        if (event.data.size === 0) return;
        localAudioChunks.push(event.data);
      };
      setAudioChunks(localAudioChunks);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prevTime) => {
          const newTime = prevTime + 1;
          if (newTime >= 3540) stopRecording(); // Stop at 59 minutes
          return newTime;
        });
      }, 1000);
    } catch (err) {
      console.error("Error starting recording:", err);
    }
  }, []);

  const stopRecording = useCallback(() => {
    setIsRecording(false);
    clearInterval(timerRef.current); // Clear timer

    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.onstop = () => {
        if (audioChunks.length > 0) {
          const audioBlob = new Blob(audioChunks, {
            type: "audio/webm;codecs=opus",
          });
          const audioUrl = URL.createObjectURL(audioBlob);
          setAudioObjArr((prev) => [...prev, { audioUrl, audioBlob }]);
          setAudioChunks([]);
        } else {
          console.error("No audio data captured on stop.");
        }
      };
    }
  }, [audioChunks, setAudioObjArr]);

  useEffect(() => {
    // Cleanup timer on component unmount
    return () => {
      clearInterval(timerRef.current);
    };
  }, []);

  return (
    <Box
      width="325px"
      height="44px"
      display="flex"
      alignItems="center"
      justifyContent="space-around"
      gap={1}
      sx={{
        background: "rgba(38, 38, 38, 0.06)",
        padding: "8px",
        borderRadius: "8px",
      }}
    >
      <svg
        height="20"
        width={"20"}
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_12439_6831)">
          <rect
            width="1.80488"
            height="20"
            rx="0.902439"
            fill={isRecording ? "blue" : "#262626"}
            fillOpacity="0.6"
          />
          <rect
            x="3.60938"
            width="1.80488"
            height="20"
            rx="0.902439"
            fill={isRecording ? "blue" : "#262626"}
            fillOpacity="0.6"
          />
          <rect
            x="7.21973"
            y="3.1582"
            width="1.80488"
            height="13.6842"
            rx="0.902439"
            fill={isRecording ? "blue" : "#262626"}
            fillOpacity="0.6"
          />
          <rect
            x="10.8291"
            y="3.1582"
            width="1.80488"
            height="13.6842"
            rx="0.902439"
            fill={isRecording ? "blue" : "#262626"}
            fillOpacity="0.6"
          />
          <rect
            x="14.4395"
            y="5.2627"
            width="1.80488"
            height="9.47368"
            rx="0.902439"
            fill={isRecording ? "blue" : "#262626"}
            fillOpacity="0.6"
          />
          <rect
            x="18.0488"
            y="5.2627"
            width="1.80488"
            height="9.47368"
            rx="0.902439"
            fill={isRecording ? "blue" : "#262626"}
            fillOpacity="0.6"
          />
          <rect
            x="21.6582"
            y="7.36816"
            width="1.80488"
            height="5.26316"
            rx="0.902439"
            fill={isRecording ? "blue" : "#262626"}
            fillOpacity="0.6"
          />
          <rect
            x="25.2686"
            y="7.36816"
            width="1.80488"
            height="5.26316"
            rx="0.902439"
            fill={isRecording ? "blue" : "#262626"}
            fillOpacity="0.6"
          />
          <rect
            x="28.8779"
            y="5.2627"
            width="1.80488"
            height="9.47368"
            rx="0.902439"
            fill={isRecording ? "blue" : "#262626"}
            fillOpacity="0.6"
          />
        </g>
      </svg>

      {isRecording ? (
        <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
          Voice note is recording:{" "}
          <strong>{formatMinutes(recordingTime)}</strong>
        </Typography>
      ) : (
        <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
          Tap the button to record voice note
        </Typography>
      )}
      <IconButton
        size="small"
        color={isRecording ? "secondary" : "primary"}
        onClick={isRecording ? stopRecording : startRecording}
      >
        {isRecording ? (
          <StopIcon fontSize="small" />
        ) : (
          <svg
            width="38"
            height="38"
            viewBox="0 0 38 38"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="19.0005"
              cy="18.9996"
              r="18.1111"
              stroke="#2C4EC5"
              strokeOpacity="0.15"
              strokeWidth="0.222222"
            />
            <circle
              cx="18.9992"
              cy="19.0002"
              r="10.7778"
              stroke="#2C4EC5"
              strokeOpacity="0.5"
              strokeWidth="0.222222"
            />
            <circle
              cx="18.9991"
              cy="19.0001"
              r="14.3333"
              stroke="#2C4EC5"
              strokeOpacity="0.3"
              strokeWidth="0.222222"
            />
            <g filter="url(#filter0_d_12439_13315)">
              <circle cx="19.0012" cy="19.0002" r="8.88889" fill="#1E1B1B" />
            </g>
            <path
              d="M18.9975 20.5557C19.9797 20.5557 20.7753 19.7601 20.7753 18.7779V16.3334C20.7753 15.3512 19.9797 14.5557 18.9975 14.5557C18.0153 14.5557 17.2197 15.3512 17.2197 16.3334V18.7779C17.2197 19.7601 18.0153 20.5557 18.9975 20.5557Z"
              stroke="white"
              strokeWidth="0.666667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15.5977 17.9561V18.7116C15.5977 20.5872 17.1221 22.1116 18.9977 22.1116C20.8732 22.1116 22.3977 20.5872 22.3977 18.7116V17.9561"
              stroke="white"
              strokeWidth="0.666667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M18.3789 16.525C18.7789 16.3784 19.2145 16.3784 19.6145 16.525"
              stroke="white"
              strokeWidth="0.666667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M18.6416 17.4676C18.8772 17.4053 19.1216 17.4053 19.3572 17.4676"
              stroke="white"
              strokeWidth="0.666667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M18.9971 22.1113V23.4447"
              stroke="white"
              strokeWidth="0.666667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </IconButton>
    </Box>
  );
};

export default VoiceRecorder;
